import React, { useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { serveFpsMultiSiteSearchExperiment } from '@atlassian/search-common/experiments';
import type { SearchUseCaseId } from '@atlassian/search-common/search-use-case-id';

import type { CloudConfig } from '../../../../common/constants/filters/cloud-config/types';
import type { SearchStoreExperience } from '../../../../common/constants/search';
import { buildSelectedSites } from '../../../../common/utils/sites';
import {
	useAggAbsoluteUrl,
	useAllFilterExcludedProducts,
	useAnalyticsAttributes,
	useApplicationMode,
	useAutoSelectPrimaryProduct,
	useAvailableProducts,
	useCloudId,
	useExperimentConfig,
	useInitialSpaceARIs,
	usePrimaryProduct,
	useProductDataRegions,
	useProductEdition,
	useQueryParams,
	useSetQueryParams,
	useThirdPartyConfigs,
	useUnitMetadata,
	useUser,
	useOrgId,
} from '../../bootstrap';
import { allFilters } from '../filters';
import { SearchStoreContainer } from '../index';
import { SearchUseCaseIdProvider } from '../search-use-case-id-context';

export const SearchStoreProvider = ({
	children,
	experience = 'search-page',
	useCaseId,
}: {
	children: React.ReactNode;
	experience?: SearchStoreExperience;
	useCaseId: SearchUseCaseId;
}): React.JSX.Element => {
	const [shouldAutoSelectPrimaryProduct, setShouldAutoSelectPrimaryProduct] = useState(true);
	const intl = useIntl();

	const [queryParams] = useQueryParams();
	const [cloudId] = useCloudId();
	const [aggAbsoluteUrl] = useAggAbsoluteUrl();
	const [initialSpaceARIs] = useInitialSpaceARIs();
	const [user] = useUser();
	const products = useAvailableProducts();
	const [allFilterExcludedProducts] = useAllFilterExcludedProducts();
	const [primaryProduct] = usePrimaryProduct();
	const [experimentConfig] = useExperimentConfig();
	const [productEdition] = useProductEdition();
	const [productDataRegions] = useProductDataRegions();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const [orgId] = useOrgId();
	const unitMetadata = useUnitMetadata();
	const [analyticsAttributes] = useAnalyticsAttributes();
	const setQueryParams = useSetQueryParams(queryParams);

	const applicationMode = useApplicationMode();

	const primaryProductToAutoSelect = useAutoSelectPrimaryProduct();

	// Gates here so consumers can treat `selectedCloudIds` as already gated. The current site is
	// listed first and is always present: `buildSelectedSites` only returns sites it can find in
	// `unitMetadata`, which is empty until that metadata loads.
	const selectedCloudIds = useMemo(
		() =>
			[
				cloudId,
				...(serveFpsMultiSiteSearchExperiment({ primaryProduct, fireExperimentExposure: false }) !==
				'control'
					? buildSelectedSites(queryParams?.sites, unitMetadata, cloudId, primaryProduct).keys()
					: []),
			].filter((id, index, all): id is string => Boolean(id) && all.indexOf(id) === index),
		[primaryProduct, queryParams?.sites, unitMetadata, cloudId],
	);

	const config = useMemo(
		() =>
			({
				useCaseId,
				aggAbsoluteUrl,
				ari: '',
				cloudId,
				selectedCloudIds,
				experimentConfig,
				initialSpaceARIs,
				// Default to an empty array so downstream `Array.isArray(config.contributorAccountIds)`
				// checks (in 3p Space / Content Under typeahead queries) succeed before any
				// contributor selection has been merged into the config by
				// loadInitialSelectFilterOptions / setSelectFilterLookupInput. The actions
				// downstream replace this with the live `allFiltersState.contributor.value` when a
				// contributor is selected.
				contributorAccountIds: [],
				intl,
				productDataRegions,
				productEdition,
				userId: user?.id || '',
				product: queryParams?.product,
				experience,
				primaryProduct,
				orgId,
				thirdPartyConfigs,
				unitMetadata,
				activationId: analyticsAttributes?.activationId,
			}) satisfies CloudConfig,
		[
			useCaseId,
			aggAbsoluteUrl,
			cloudId,
			selectedCloudIds,
			experimentConfig,
			initialSpaceARIs,
			intl,
			productDataRegions,
			productEdition,
			user?.id,
			queryParams?.product,
			experience,
			primaryProduct,
			orgId,
			thirdPartyConfigs,
			unitMetadata,
			analyticsAttributes?.activationId,
		],
	);

	const queryParamsInUse = useMemo(() => {
		const {
			lastModified_lt: _lastModified_lt,
			lastModified_gt: _lastModified_gt,
			...queryParamsInUse
		} = queryParams;
		return queryParamsInUse;
	}, [queryParams]);

	// primaryProductToAutoSelect will be set to the primary product if the user is in unified search
	// and no product is selected
	// This is to ensure that the primary product is selected by default for unified search
	useEffect(() => {
		if (products && products.length > 0) {
			if (shouldAutoSelectPrimaryProduct && primaryProductToAutoSelect) {
				setQueryParams({ ...queryParams, product: primaryProductToAutoSelect });
			}
			setShouldAutoSelectPrimaryProduct(false);
		}
	}, [
		applicationMode,
		primaryProduct,
		primaryProductToAutoSelect,
		products,
		queryParams,
		setQueryParams,
		shouldAutoSelectPrimaryProduct,
	]);

	return (
		<SearchUseCaseIdProvider value={useCaseId}>
			<SearchStoreContainer
				queryParams={queryParamsInUse}
				allFilters={allFilters}
				config={config}
				setQueryParams={setQueryParams}
				availableProducts={products}
				allFilterExcludedProducts={allFilterExcludedProducts}
				applicationMode={applicationMode}
				primaryProduct={primaryProduct}
				thirdPartyConfigs={thirdPartyConfigs}
				experience={experience}
			>
				{children}
			</SearchStoreContainer>
		</SearchUseCaseIdProvider>
	);
};
